/* global BigInt */
import { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { TruckIcon } from "@heroicons/react/20/solid";
import { useForm } from "react-hook-form";
import { Button, TextInput, Select, Label, Alert } from "flowbite-react";
import { CheckCircle, XCircle } from "lucide-react";

export function DeclareTransferModalAdminV({
  open,
  onSuccessClose,
  onClose,
  adminV,
  entity,
  torole,
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm();

  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });
  const [quantity, setQuantity] = useState("");
  const [max, setMax] = useState(0);

  const selectedBatchId = watch("batch_id");

  useEffect(() => {
    fetchBatches();
  }, []);

  useEffect(() => {
    if (selectedBatchId) {
      fetchFirstICCID();
    }
  }, [selectedBatchId]);

  useEffect(() => {
    if (selectedBatchId && quantity) {
      fetchLastICCID();
    }
  }, [quantity, selectedBatchId]);

  const fetchBatches = async () => {
    try {
      let Url = `${process.env.REACT_APP_BACKEND_URL}batch/getAll`;
      if (torole === "franchise" || torole === "PDV") {
        Url = `${process.env.REACT_APP_BACKEND_URL}batch/userRelatedBatches`;
      }
      const response = await axios.get(Url, { withCredentials: true });
      setBatches(response.data.data);
    } catch (error) {
      console.error("Error fetching batches:", error);
    }
  };

  const fetchFirstICCID = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}getRangeIccid?batchId=${selectedBatchId}&target=first`,
        { withCredentials: true }
      );
      setValue("first_iccid", response.data.first.ICCID);
      setMax(response.data.total);
      setAlert({
        show: true,
        type: "success",
        message: "Success fetching first ICCID",
      });
    } catch (error) {
      setAlert({
        show: true,
        type: "error",
        message: error.response.data.message ||"Erreur lors de la récupération du premier ICCID",
      });
      setValue("first_iccid", "");
      setValue("last_iccid", "");
      setMax(0)
    }
  };

  const fetchLastICCID = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}getRangeIccid?batchId=${selectedBatchId}&target=last&quantity=${quantity}`,
        { withCredentials: true }
      );
      setValue("last_iccid", response.data.last.ICCID);
      setAlert({
        show: true,
        type: "success",
        message: "Success fetching last ICCID",
      });
    } catch (error) {
      setAlert({
        show: true,
        type: "error",
        message: error.response.data.message  || "Erreur lors de la récupération du dernier ICCID",
      });
      setValue("first_iccid", "");
      setValue("last_iccid", "");
      setMax(0)
      fetchFirstICCID();

    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setAlert({ show: false, type: "", message: "" });

      data.receiver = entity?.User || entity?.user;
      data.type = "affectation";

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}createTransaction`,
        data,
        {
          withCredentials: true,
        }
      );

      if (response.status === 200) {
        if (response.data.isValid) {
          setAlert({
            show: true,
            type: "success",
            message: "Transfert déclaré avec succès",
          });
          setTimeout(() => {
            onSuccessClose();
          }, 2000);
        } else {
          setAlert({
            show: true,
            type: "warning",
            message: "Transaction invalide. Veuillez vérifier les données.",
          });
        }
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setAlert({
        show: true,
        type: "error",
        message: error.response?.data?.message || "Erreur lors du transfert",
      });
      console.error("Error during transfer:", error);
    }
  };

  const renderAlert = () => {
    if (!alert.show) return null;

    const alertProps = {
      error: { color: "failure", icon: XCircle },
      success: { color: "success", icon: CheckCircle },
      warning: { color: "warning", icon: CheckCircle },
    }[alert.type];

    return (
      <Alert
        color={alertProps.color}
        icon={alertProps.icon}
        onDismiss={() => setAlert({ show: false, type: "", message: "" })}
        className="mb-4"
      >
        {alert.message}
      </Alert>
    );
  };

  return (
    <Dialog className="relative z-50" open={open} onClose={onClose}>
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4 dark:bg-gray-700">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                  <TruckIcon
                    className="h-6 w-6 text-blue-600"
                    aria-hidden="true"
                  />
                </div>
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <DialogTitle
                    as="h3"
                    className="text-base font-semibold leading-6 text-gray-900 dark:text-white"
                  >
                    Déclarer un transfert
                  </DialogTitle>
                  {renderAlert()}
                  <form
                    id="form-1"
                    className="flex flex-col gap-4"
                    onSubmit={handleSubmit(onSubmit)}
                  >
                    <div>
                      <p className="text-sm text-gray-800 dark:text-gray-300">
                        Vous êtes sur le point de déclarer un transfert de stock
                        de{" "}
                        <span className="font-bold">
                          {torole === "PDV"
                            ? adminV?.owner
                            : adminV?.FirstName + " " + adminV?.LastName}
                        </span>{" "}
                        vers{" "}
                        {torole === "franchise" ? (
                          <>
                            la Franchise{" "}
                            <span className="font-bold">
                              ({entity?.pos_franchise_id?.name})
                            </span>
                          </>
                        ) : torole === "PDV" ? (
                          <>
                            le PDV
                            <span className="font-bold">
                              {" "}
                              ({entity?.owner}){" "}
                            </span>
                          </>
                        ) : (
                          <>
                            le Commercial{" "}
                            <span className="font-bold">
                              {" "}
                              ({entity?.FirstName +
                                " " +
                                entity?.LastName}){" "}
                            </span>
                          </>
                        )}
                        . Veuillez entrer les détails ci-dessous.
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="batchName">Batches</Label>
                      <Select
                        id="batchName"
                        {...register("batch_id", { required: true })}
                        required
                        defaultValue={""}
                      >
                        <option value="" className="text-slate-400" disabled>
                          Sélectionner un batch
                        </option>
                        {batches.map((batch) => (
                          <option
                            key={batch?._id}
                            value={batch?._id}
                            className="text-gray-900 dark:text-white"
                          >
                            {batch?.batch_name}
                          </option>
                        ))}
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="first_iccid">Premier ICCID</Label>
                      <TextInput
                        id="first_iccid"
                        type="text"
                        disabled
                        {...register("first_iccid")}
                        color={errors.first_iccid ? "failure" : "gray"}
                      />
                    </div>
                    <div>
                      <Label htmlFor="quantity">Quantité</Label>
                      <TextInput
                        id="quantity"
                        type="number"
                        min="1"
                        value={quantity}
                        onChange={(e) => {
                          if (e.target.value > max) {
                            setAlert({
                              show: true,
                              type: "error",
                              message:"Quantity is greater then the Max!!!",
                            });
                            setQuantity(0)
                            return;
                          }
                          setQuantity(e.target.value);
                        }}
                        placeholder={`entrer la quantité (Max : ${max})`}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="last_iccid">Dernier ICCID</Label>
                      <TextInput
                        id="last_iccid"
                        type="text"
                        disabled
                        {...register("last_iccid")}
                        color={errors.last_iccid ? "failure" : "gray"}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Button
                        type="submit"
                        color={"dark"}
                        size={"sm"}
                        form="form-1"
                        isProcessing={loading}
                      >
                        Accepter
                      </Button>
                      <Button color={"dark"} size={"sm"} onClick={onClose}>
                        Annuler
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 dark:bg-gray-700"></div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
